import { DashboardCard } from "@dashboard/components/Card";
import CardSpacer from "@dashboard/components/CardSpacer";
import {
  useWsmFitmentProductQualifiersQuery,
  useWsmFitmentProductSchemasQuery,
  useWsmProductFitmentQuery,
} from "@dashboard/graphql";
import { buttonMessages } from "@dashboard/intl";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { fitmentMessages } from "./messages";
import ProductFitmentSchemaCard from "./ProductFitmentSchemaCard";
import ProductQualifierValuesCard from "./ProductQualifierValuesCard";
import { useAllPages } from "./useAllPages";
import { appendConnection, qualifierOfValue, qualifierValueOptions } from "./utils";

export interface ProductFitmentProps {
  productId: string;
}

/**
 * The Fitments tab: one card per active schema, plus a read-only card for any
 * inactive schema the product still has a row under (so the merchant can see
 * and forget it), plus the product's own qualifier values, all read from and written to wsm/fitment in Saleor
 * (wsm-saleor/wsm/fitment/README.md). Each card saves itself; nothing here
 * rides on the product form's Save.
 */
/**
 * The latest non-empty value, for as long as `owner` stays the same: a
 * re-read that fails (errorPolicy "all" can hand back a null field) must not
 * turn a product's saved rows into "unknown" under the merchant's edits.
 */
const useLastGood = <T,>(owner: string, value: T | null | undefined): T | undefined => {
  const held = React.useRef<{ owner: string; value: T } | null>(null);

  if (value !== null && value !== undefined) {
    held.current = { owner, value };
  } else if (held.current?.owner !== owner) {
    held.current = null;
  }

  return held.current?.value;
};

// Only the first read goes to the network; the reobserve that ends every
// fetchMore in Apollo 3.4 then reads the merged pages from the cache instead
// of fetching page 1 again over them (see useAllPages).
const VOCABULARY_QUERY_OPTIONS = { nextFetchPolicy: "cache-first" } as const;

export const ProductFitment = ({ productId }: ProductFitmentProps) => {
  const intl = useIntl();
  const schemasQuery = useWsmFitmentProductSchemasQuery(VOCABULARY_QUERY_OPTIONS);
  const qualifiersQuery = useWsmFitmentProductQualifiersQuery(VOCABULARY_QUERY_OPTIONS);
  // Both vocabularies page at 100; read every page before rendering so no
  // schema card or qualifier value is silently missing.
  const schemaPages = useAllPages(
    schemasQuery,
    data => data?.wsmFitmentSchemas,
    (held, page) => ({
      ...page,
      wsmFitmentSchemas: appendConnection(held.wsmFitmentSchemas, page.wsmFitmentSchemas),
    }),
  );
  const qualifierPages = useAllPages(
    qualifiersQuery,
    data => data?.wsmFitmentQualifiers,
    (held, page) => ({
      ...page,
      wsmFitmentQualifiers: appendConnection(held.wsmFitmentQualifiers, page.wsmFitmentQualifiers),
    }),
  );
  const fitmentQuery = useWsmProductFitmentQuery({
    variables: { ids: [productId] },
    fetchPolicy: "cache-and-network",
  });
  const schemaConnection = useLastGood(productId, schemasQuery.data?.wsmFitmentSchemas);
  const qualifierConnection = useLastGood(productId, qualifiersQuery.data?.wsmFitmentQualifiers);
  const fitmentConnection = useLastGood(productId, fitmentQuery.data?.wsmFitmentProducts);
  const fitment = fitmentConnection?.edges[0]?.node;
  const rowFor = (schemaId: string) => fitment?.schemas.find(row => row.schema.id === schemaId);
  const allSchemas = mapEdgesToItems(schemaConnection) ?? [];
  const activeCount = allSchemas.filter(schema => schema.isActive).length;
  const schemas = allSchemas.filter(schema => schema.isActive || rowFor(schema.id));
  const qualifiers = React.useMemo(
    () => mapEdgesToItems(qualifierConnection) ?? [],
    [qualifierConnection],
  );
  const options = React.useMemo(() => qualifierValueOptions(qualifiers), [qualifiers]);
  const qualifierOf = React.useMemo(() => qualifierOfValue(qualifiers), [qualifiers]);
  const savedQualifierIds = React.useMemo(() => fitment?.qualifierValueIds ?? [], [fitment]);
  const hasData = !!schemaConnection && !!qualifierConnection && !!fitmentConnection;
  const failed =
    !!schemasQuery.error ||
    !!qualifiersQuery.error ||
    !!fitmentQuery.error ||
    schemaPages.error ||
    qualifierPages.error;
  // Once the cards are up they stay up: a later page walk (a vocabulary
  // re-read that reset the list) or a failed re-read must not unmount them
  // and drop their unsaved edits.
  const shown = React.useRef(false);
  const ready = hasData && !schemaPages.paging && !qualifierPages.paging;

  if (ready && !failed) {
    shown.current = true;
  }

  // The re-read after a card's write. errorPolicy "all" can resolve a failed
  // read instead of rejecting it; the card is told either way.
  const refetch = () =>
    fitmentQuery.refetch().then(result => {
      if (result.error || (result.errors?.length ?? 0) > 0 || !result.data?.wsmFitmentProducts) {
        throw result.error ?? new Error("fitment re-read failed");
      }

      return result;
    });
  const ignore = () => undefined;
  const retry = () => {
    if (schemasQuery.error) {
      schemasQuery.refetch().catch(ignore);
    }

    if (qualifiersQuery.error) {
      qualifiersQuery.refetch().catch(ignore);
    }

    if (fitmentQuery.error || !fitmentConnection) {
      fitmentQuery.refetch().catch(ignore);
    }

    schemaPages.retry();
    qualifierPages.retry();
  };

  if (failed && !(shown.current && hasData)) {
    return (
      <DashboardCard data-test-id="fitment-load-error">
        <DashboardCard.Content>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={4}>
            <Text size={3} color="critical1">
              {intl.formatMessage(fitmentMessages.loadFailed)}
            </Text>
            <Button variant="secondary" onClick={retry} data-test-id="fitment-load-retry">
              {intl.formatMessage(buttonMessages.retry)}
            </Button>
          </Box>
        </DashboardCard.Content>
      </DashboardCard>
    );
  }

  if (!shown.current) {
    return (
      <DashboardCard>
        <DashboardCard.Content>
          <Skeleton />
        </DashboardCard.Content>
      </DashboardCard>
    );
  }

  return (
    <Box data-test-id="product-fitment">
      {failed && (
        <>
          <DashboardCard data-test-id="fitment-refresh-error">
            <DashboardCard.Content>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={4}>
                <Text size={3} color="critical1">
                  {intl.formatMessage(fitmentMessages.refreshFailed)}
                </Text>
                <Button variant="secondary" onClick={retry} data-test-id="fitment-refresh-retry">
                  {intl.formatMessage(buttonMessages.retry)}
                </Button>
              </Box>
            </DashboardCard.Content>
          </DashboardCard>
          <CardSpacer />
        </>
      )}
      {activeCount === 0 && (
        <>
          <DashboardCard data-test-id="fitment-no-schemas">
            <DashboardCard.Content>
              <Text size={3} color="default2">
                {intl.formatMessage(fitmentMessages.noSchemas)}
              </Text>
            </DashboardCard.Content>
          </DashboardCard>
          <CardSpacer />
        </>
      )}
      {schemas.map(schema => (
        <React.Fragment key={schema.id}>
          <ProductFitmentSchemaCard
            productId={productId}
            schema={schema}
            row={rowFor(schema.id)}
            readOnly={!schema.isActive}
            qualifierValueOptions={options}
            qualifierOf={qualifierOf}
            onChanged={refetch}
          />
          <CardSpacer />
        </React.Fragment>
      ))}
      <ProductQualifierValuesCard
        productId={productId}
        qualifierValueOptions={options}
        savedIds={savedQualifierIds}
        onChanged={refetch}
      />
    </Box>
  );
};

export default ProductFitment;
